import { matchPath } from 'react-router-dom';

export const PAGE_TITLE_PREFIX = 'WMS';

export interface PageTitleExtras {
  readonly orderNumber?: string | null;
}

interface RouteTitle {
  readonly path: string;
  readonly segment: (
    params: Readonly<Record<string, string | undefined>>,
    extras: PageTitleExtras,
  ) => string;
  /** Static page H1 shown in the global navbar. Omit when the page H1 is dynamic or missing. */
  readonly navbarTitle?: string;
}

function decodeParam(value: string | undefined): string {
  if (!value) return '';
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function orderSegment(_params: Readonly<Record<string, string | undefined>>, extras: PageTitleExtras): string {
  const orderNumber = extras.orderNumber?.trim();
  return orderNumber ? `Order ${orderNumber}` : 'Order';
}

function bundleSegment(params: Readonly<Record<string, string | undefined>>): string {
  const sku = decodeParam(params.parentSku);
  return sku ? `Bundle ${sku}` : 'Bundle';
}

function shipmentSegment(params: Readonly<Record<string, string | undefined>>): string {
  return params.shipmentId === 'all' ? 'All Shipments' : 'Shipment';
}

const ROUTE_TITLES: readonly RouteTitle[] = [
  { path: '/', segment: () => 'Home' },
  { path: '/configure', segment: () => 'Configure', navbarTitle: 'Configure My Account' },
  { path: '/order-manager', segment: () => 'Orders', navbarTitle: 'Order Management' },
  { path: '/orders-list', segment: () => 'All Orders', navbarTitle: 'All Orders' },
  { path: '/my-orders', segment: () => 'My Orders', navbarTitle: 'My Orders' },
  { path: '/single-sku-orders', segment: () => 'Batch Orders', navbarTitle: 'Single-Item by SKU' },
  { path: '/bundle-work-orders', segment: () => 'Make Bundles', navbarTitle: 'Bundle Work Orders' },
  { path: '/pick-next-order', segment: () => 'Pick Next Order' },
  { path: '/claim-order', segment: () => 'Claim Order' },
  { path: '/scan-label', segment: () => 'Scan Label', navbarTitle: 'Scan Label' },
  { path: '/tasks/new', segment: () => 'New Task', navbarTitle: 'Task Queue' },
  { path: '/tasks/:id', segment: () => 'Task', navbarTitle: 'Task Queue' },
  { path: '/tasks', segment: () => 'Task Queue', navbarTitle: 'Task Queue' },
  { path: '/admin/task-tags', segment: () => 'Task Tags', navbarTitle: 'Task tags' },
  { path: '/admin/recurring-tasks', segment: () => 'Recurring Tasks', navbarTitle: 'Recurring tasks' },
  { path: '/product-weight', segment: () => 'Product Weight', navbarTitle: 'Product Weight' },
  { path: '/shelf-indexer', segment: () => 'Shelf Indexer', navbarTitle: 'Shelf Indexer' },
  { path: '/search-warehouse', segment: () => 'Search Warehouse', navbarTitle: 'Search Warehouse' },
  { path: '/order/:orderId', segment: orderSegment },
  { path: '/bundle-work-order/:parentSku', segment: bundleSegment },
  { path: '/filter', segment: () => 'Filter' },
  { path: '/dev', segment: () => 'Dev Panel' },
  { path: '/dev/inv-diff', segment: () => 'Inv Diff' },
  { path: '/dev/ocr-ab', segment: () => 'OCR A/B' },
  { path: '/shelf-audit', segment: () => 'Shelf Audit', navbarTitle: 'Shelf Audit' },
  { path: '/inventory-counts', segment: () => 'Inventory Counts', navbarTitle: 'Inventory Counts' },
  { path: '/inventory-transfers', segment: () => 'Inventory Transfers', navbarTitle: 'Inventory Transfers' },
  { path: '/inventory-transfers/:transferId', segment: () => 'Inventory Transfer' },
  { path: '/print-warehouse-labels', segment: () => 'Warehouse Labels', navbarTitle: 'Warehouse Labels' },
  { path: '/print-warehouse-labels/print', segment: () => 'Print Labels', navbarTitle: 'Print Warehouse Labels' },
  {
    path: '/print-warehouse-labels/check-label-out',
    segment: () => 'Check Label Out',
    navbarTitle: 'Label Register',
  },
  { path: '/print-warehouse-labels/reprint', segment: () => 'Print Log', navbarTitle: 'Print Log' },
  { path: '/admin', segment: () => 'Admin Panel', navbarTitle: 'Admin Panel' },
  { path: '/admin/general-settings', segment: () => 'General Settings', navbarTitle: 'General Settings' },
  { path: '/admin/dashboard', segment: () => 'Dashboard', navbarTitle: 'Activity Dashboard' },
  { path: '/admin/past-performance', segment: () => 'Past Performance', navbarTitle: 'Past Performance' },
  {
    path: '/admin/shelf-manage',
    segment: () => 'Shelf Manage',
    navbarTitle: 'Create or Delete Shelf Location',
  },
  {
    path: '/admin/bundle-scan-subset',
    segment: () => 'Bundle Parent Scans',
    navbarTitle: 'Manage Bundle Parent Scans',
  },
  {
    path: '/admin/bundle-defaults',
    segment: () => 'Bundle Defaults',
    navbarTitle: 'Manage Bundle Default Components',
  },
  {
    path: '/admin/bundle-thresholds',
    segment: () => 'Bundle Thresholds',
    navbarTitle: 'Manage Bundle Thresholds',
  },
  { path: '/space-calculator', segment: () => 'Space Calculator', navbarTitle: 'Warehouse Stacking Calculator' },
  { path: '/receive', segment: () => 'Receive', navbarTitle: 'Shipments' },
  { path: '/receive/shipment/:shipmentId', segment: shipmentSegment },
  { path: '/receive/shipment/:shipmentId/item/:shipmentItemId', segment: () => 'Receive Item' },
];

export function formatPageTitle(segment: string): string {
  const trimmed = segment.trim();
  return trimmed === '' ? PAGE_TITLE_PREFIX : `${PAGE_TITLE_PREFIX} ${trimmed}`;
}

export function titleSegmentForPathname(pathname: string, extras: PageTitleExtras = {}): string {
  for (const route of ROUTE_TITLES) {
    const match = matchPath({ path: route.path, end: true }, pathname);
    if (match) {
      return route.segment(match.params, extras);
    }
  }
  return '';
}

export function navbarTitleForPathname(pathname: string): string {
  for (const route of ROUTE_TITLES) {
    const match = matchPath({ path: route.path, end: true }, pathname);
    if (match) {
      return route.navbarTitle ?? '';
    }
  }
  return '';
}
